/* GW Scroll Carousel — <gw-scroll-carousel>
   Shared scroll-snap carousel chrome, extracted from gw-judgeme-reviews.js so
   gw-product-carousel and gw-judgeme-reviews share one implementation.

   Markup contract — all hooks are optional except the track:
     <gw-scroll-carousel>
       <button data-gwsc-prev>  <button data-gwsc-next>   (optional)
       <ul|div data-gwsc-track> ...items...                (required)
       <div data-gwsc-indicator><div data-gwsc-thumb>      (optional)
       <div data-gwsc-dots><button data-gwsc-dot>...       (optional, one
                                                            per snap point)
     </gw-scroll-carousel>

   Dots (added for the cart drawer's recommendations carousel): the Nth
   [data-gwsc-dot] pages to the Nth snap point, and sync() marks the dot of
   the current snap point with aria-current="true". Without dots nothing
   changes for the carousels that shipped before them.

   Paging: forward to the first scroll-snap point that clears a whole track
   viewport. The snap points are read from the DOM rather than assumed, so the
   CSS alone decides what a page is — which is what lets one element serve a
   1-row desktop / 2-row mobile grid (a column holds two items and therefore
   one snap point) and a peeking single-row track. This replaces the old
   card-width * 4 arithmetic, which could not express the 2-row case.

   Content that arrives later (gw-judgeme-reviews renders from an API after
   connectedCallback) calls refresh() — listeners bind once, measurements
   are always taken live. */

const REDUCED = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class GwScrollCarousel extends HTMLElement {
  connectedCallback() {
    /* The window listener lives only while the element is connected: the
       cart drawer re-creates its carousel on every cart change, and a
       listener per discarded instance would pile up for the page's life. */
    if (!this.onResize) this.onResize = () => this.sync();
    window.addEventListener('resize', this.onResize, { passive: true });
    this.refresh();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    if (this.raf) cancelAnimationFrame(this.raf);
    clearTimeout(this.guard);
  }

  get track() {
    return this.querySelector('[data-gwsc-track]');
  }

  /* The CSS decides where a page may land, via scroll-snap-align. Reading
     those points back is what lets one component serve a track that snaps
     every item and one that snaps every fourth. Items sharing a left edge
     (a 2-row grid column) collapse to a single point. Falls back to every
     item when the track declares no snap points at all. */
  starts() {
    const track = this.track;
    if (!track) return [];
    const base = track.getBoundingClientRect().left - track.scrollLeft;
    const all = [...track.children];
    const snapping = all.filter(
      (el) => getComputedStyle(el).scrollSnapAlign.split(' ')[0] === 'start'
    );
    const seen = [];
    for (const el of snapping.length ? snapping : all) {
      const x = Math.round(el.getBoundingClientRect().left - base);
      if (!seen.some((v) => Math.abs(v - x) < 2)) seen.push(x);
    }
    return seen.sort((a, b) => a - b);
  }

  /* Advance to the first snap point that clears a whole viewport, so a page
     turns over completely rather than stopping on the last item still on
     screen. Landing short of a snap point is not an option: with
     `scroll-snap-type: mandatory` the browser drags the scroll back to a snap
     point as soon as it settles, which reads as the arrow doing nothing. */
  target(dir) {
    const track = this.track;
    const starts = this.starts();
    if (!track || !starts.length) return null;
    const from = track.scrollLeft;
    const want = from + dir * track.clientWidth;

    if (dir > 0) {
      const ahead = starts.filter((o) => o > from + 1);
      if (!ahead.length) return null;
      const clears = ahead.filter((o) => o >= want - 1);
      return clears.length ? Math.min(...clears) : Math.max(...ahead);
    }
    const behind = starts.filter((o) => o < from - 1);
    if (!behind.length) return null;
    const clears = behind.filter((o) => o <= want + 1);
    return clears.length ? Math.max(...clears) : Math.min(...behind);
  }

  go(dir) {
    const to = this.target(dir);
    if (to === null) return;
    this.animateTo(to);
  }

  /* The scroll is animated by hand rather than with behavior:'smooth'.
     Native smooth scrolling is silently dropped on this track — measured:
     scrollTo({behavior:'smooth'}) and CSS scroll-behavior:smooth both leave
     scrollLeft at 0 while an instant assignment moves it correctly, with or
     without scroll-snap-type. A requestAnimationFrame tween has no such
     dependency and behaves the same in every browser.

     Snapping is suspended for the duration: `mandatory` re-snaps to the
     nearest point on every settled frame, which would drag a tween in
     progress back to where it started. The destination is itself a snap
     point, so nothing is out of alignment when it is restored. */
  animateTo(to) {
    const track = this.track;
    if (this.raf) cancelAnimationFrame(this.raf);

    /* Nothing to animate for a reader who asked for no motion, or for a page
       that is not being painted — a hidden document runs no animation frames
       at all, so a tween there would never advance and would strand the
       suspended snap type below. */
    if (REDUCED() || document.hidden) {
      track.scrollLeft = to;
      this.sync();
      return;
    }

    const from = track.scrollLeft;
    const dist = to - from;
    if (!dist) return;

    const started = performance.now();
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    track.style.scrollSnapType = 'none';

    const settle = () => {
      this.raf = null;
      clearTimeout(this.guard);
      track.style.scrollSnapType = '';
      this.sync();
    };

    const step = (now) => {
      const p = Math.min((now - started) / 420, 1);
      track.scrollLeft = from + dist * ease(p);
      if (p < 1) {
        this.raf = requestAnimationFrame(step);
      } else {
        settle();
      }
    };

    /* If the frames stop arriving mid-tween (the tab is backgrounded on the
       way), land on the destination rather than leaving the track part-scrolled
       with snapping still suspended. */
    clearTimeout(this.guard);
    this.guard = setTimeout(() => {
      if (this.raf) cancelAnimationFrame(this.raf);
      track.scrollLeft = to;
      settle();
    }, 900);

    this.raf = requestAnimationFrame(step);
  }

  sync() {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;

    const prev = this.querySelector('[data-gwsc-prev]');
    const next = this.querySelector('[data-gwsc-next]');
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;

    /* No overflow at all: the controls have nothing to drive. Hiding them
       rather than leaving them permanently disabled keeps a short collection
       from shipping dead chrome. */
    const idle = max <= 1;
    this.toggleAttribute('data-gwsc-idle', idle);

    const thumb = this.querySelector('[data-gwsc-thumb]');
    const rail = this.querySelector('[data-gwsc-indicator]');
    if (thumb && rail) {
      /* Thumb length is the visible fraction of the scrollable content, so it
         reports the real list length rather than a fixed design width. Opt-in:
         without the attribute the width stays whatever CSS set, which is what
         keeps gw-judgeme-reviews rendering exactly as it shipped. */
      if (thumb.hasAttribute('data-gwsc-thumb-auto')) {
        const frac = idle ? 1 : track.clientWidth / track.scrollWidth;
        thumb.style.width = `${Math.max(frac * 100, 8)}%`;
      }
      const travel = rail.clientWidth - thumb.offsetWidth;
      const pos = idle ? 0 : (track.scrollLeft / max) * travel;
      thumb.style.transform = `translateX(${pos}px)`;
    }

    const dots = this.querySelectorAll('[data-gwsc-dot]');
    if (dots.length) {
      /* The current page is the last snap point the scroll has reached; at
         the far end it is the last one even when the final points sit
         closer together than a viewport. */
      const starts = this.starts();
      let current = 0;
      starts.forEach((start, i) => {
        if (track.scrollLeft >= start - 2) current = i;
      });
      if (!idle && track.scrollLeft >= max - 1) current = starts.length - 1;
      dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
    }
  }

  refresh() {
    const track = this.track;
    if (!track) return;

    if (!this.bound) {
      this.bound = true;
      const prev = this.querySelector('[data-gwsc-prev]');
      const next = this.querySelector('[data-gwsc-next]');
      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));
      this.querySelectorAll('[data-gwsc-dot]').forEach((dot, i) => {
        dot.addEventListener('click', () => {
          const to = this.starts()[i];
          if (to !== undefined) this.animateTo(to);
        });
      });
      track.addEventListener('scroll', () => this.sync(), { passive: true });
    }

    this.sync();
  }
}

if (!customElements.get('gw-scroll-carousel')) {
  customElements.define('gw-scroll-carousel', GwScrollCarousel);
}

if (window.wetheme && window.wetheme.webcomponentRegistry) {
  window.wetheme.webcomponentRegistry.register({
    key: 'gw-scroll-carousel',
    element: 'gw-scroll-carousel',
  });
}
